<template>
  <div v-click-outside="hideMenu" class="user-menu">
    <button class="user-menu__toggle" @click="view.toggleMenu">
      <div class="user-menu__avatar">
        <span class="user-menu__avatar-icon iconfont iaccount"></span>
      </div>
      <span class="user-menu__arrow iconfont iarrow-down"></span>
    </button>

    <div
      v-if="view.changelog && showChangelog()"
      class="user-menu__changelog-badge-container"
    >
      <div class="user-menu__changelog-badge">
        <ChangelogBadge :view="view.changelog" />
      </div>
    </div>

    <transition name="fade-transition">
      <ul v-if="view.isMenuVisible" class="user-menu__list">
        <li v-if="!isTemporaryUser()" class="user-menu__item">
          <router-link :to="routeName('billing')" class="user-menu__link">
            <span class="user-menu__icon iconfont iicon-07"></span>
            Billing
          </router-link>
        </li>
        <li class="user-menu__item">
          <router-link
            :to="routeName('all_highlights')"
            class="user-menu__link"
          >
            <span class="user-menu__icon iconfont ihighlights"></span>
            Highlights
          </router-link>
        </li>
        <li v-if="!isTemporaryUser()" class="user-menu__item">
          <router-link :to="routeName('settings')" class="user-menu__link">
            <span class="user-menu__icon iconfont isettings"></span>
            Settings
          </router-link>
        </li>
        <li class="user-menu__item">
          <router-link :to="routeName('help')" class="user-menu__link">
            <span class="user-menu__icon iconfont iicon-08"></span>
            Support
          </router-link>
        </li>
        <li v-if="showLanguage()" class="user-menu__item">
          <LanguageSwitcher />
        </li>
        <li v-if="showChangelog()" class="user-menu__item">
          <ChangelogPopupButton
            class="user-menu__link"
            :view="view.changelog"
            :header-view="view"
          />
        </li>
        <li
          v-if="isTemporaryUser()"
          class="user-menu__item user-menu__item--login"
        >
          <router-link class="user-menu__link" :to="{ name: 'login' }">
            <span
              class="user-menu__icon user-menu__icon--login iconfont ilogin"
            ></span>
            Log in
          </router-link>
        </li>
        <li v-else class="user-menu__item user-menu__item--logout">
          <a class="user-menu__link" @click="view.logout()">
            <span class="user-menu__icon iconfont iicon-09"></span>
            Log out
          </a>
        </li>
        <li class="user-menu__item user-menu__item--night-mode">
          <div class="user-menu__link">
            <span class="user-menu__icon iconfont inight-mode"></span>
            Dark mode
          </div>
          <button
            class="user-menu__night-mode-toggle"
            @click="toggleNightMode()"
          >
            <div
              class="user-menu__night-mode-toggle-button"
              :class="{
                'user-menu__night-mode-toggle-button--active':
                  isNightModeActive,
              }"
            ></div>
          </button>
        </li>
      </ul>
    </transition>
    <BasePopup ref="logoutFailurePopup" title="Logout Failure">
      We couldn't log you out right now as you seem to be offline. Please check
      your internet connection and then try again.
    </BasePopup>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'

import { HeaderView } from '@/models/header'
import { nightMode } from '@/services/night'
import BasePopup from '@/components/ui/BasePopup.vue'
import { emitter } from '@/services/mitt'
import LanguageSwitcher from '@/app/components/header/LanguageSwitcher.vue'
import ChangelogBadge from '@/app/components/changelog/Badge.vue'
import ChangelogPopupButton from '@/app/components/changelog/PopupButton.vue'
import { auth } from '@/services/auth'

const props = defineProps<{
  view: HeaderView
}>()

const logoutFailurePopup = ref<InstanceType<typeof BasePopup> | null>(null)

const isNightModeActive = ref(false)

function routeName(link: string): Record<string, string> {
  return { name: link }
}

function showChangelog(): boolean {
  return props.view.changelog !== undefined
}

function showLanguage(): boolean {
  return props.view.showLanguage
}

function isTemporaryUser(): boolean {
  return auth.isTemporaryUser()
}

async function toggleNightMode(): Promise<void> {
  await props.view.toggleNightMode()
  emitter.emit('toggleNightMode')
}

onMounted(() => {
  isNightModeActive.value = nightMode.active
  emitter.on('logoutOfflineFailure', () => {
    logoutFailurePopup.value?.show()
  })
  emitter.on('toggleNightMode', async () => {
    isNightModeActive.value = nightMode.active
  })
})

/**
 * Proxy function to call the view.hideMenu()
 *
 * This function is necessary for Vue to properly handle
 * reactivity and set the view._isMenuHidden property on
 * click outside.
 */
function hideMenu(): void {
  props.view.hideMenu()
}
</script>

<style lang="scss" scoped>
.user-menu {
  position: relative;

  &__toggle {
    display: flex;
    align-items: center;
    color: $c-black-2;
    cursor: pointer;
    transition: all 0.2s ease-in-out;
    position: relative;
    @include hover {
      color: $c-black-2;
      .user-menu__avatar {
        background-color: $c-yellow;
        border-color: $c-yellow;
      }
    }
  }
  &__avatar {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    border: 1px solid $c-border-2;
    display: flex;
    justify-content: center;
    align-items: center;
    transition: all 0.2s ease-in-out;
    @media only screen and (max-width: $w-max-content-width) {
      width: 40px;
      height: 40px;
    }
    @media only screen and (max-width: $w-370) {
      width: 32px;
      height: 32px;
    }
  }
  &__avatar-icon {
    font-size: 24px;
    margin-top: 1px;
    @media only screen and (max-width: $w-370) {
      font-size: 20px;
    }
  }
  &__arrow {
    font-size: 24px;
    margin-left: 4px;
    margin-top: 2px;
    @media only screen and (max-width: $w-370) {
      font-size: 16px;
      margin-left: 2px;
    }
  }
  &__list {
    position: absolute;
    top: calc(100% + 12px);
    left: -170px;
    z-index: 4;
    width: 266px;
    max-height: 75dvh;
    overflow-y: auto;
    background-color: $c-white;
    border-radius: 8px;
    filter: drop-shadow(0px 1px 5px rgba(255, 255, 255, 0.1))
      drop-shadow(0px 8px 15px rgba(20, 28, 49, 0.1));
    padding: 20px 10px;
    cursor: auto;
    @media only screen and (max-width: $w-tablet-s) {
      right: 0;
      left: auto;
    }
  }
  &__item {
    font-size: 16px;
    line-height: 28px;
    padding: 10px 15px;
    border-radius: 4px;
    &--logout {
      color: $c-black-6;
    }
    &--night-mode {
      border-top: 1px solid $c-border;
      padding-top: 12px;
      display: flex;
      justify-content: space-between;
    }
    @include hover {
      color: $c-black-2;
      @media only screen and (min-width: $w-tablet-b) {
        background: rgba($color: $c-bg, $alpha: 0.5);
      }
      @media only screen and (max-width: $w-tablet-s) {
        color: $c-black-2;
      }
    }
  }
  &__link {
    display: flex;
    align-items: center;
    gap: 12px;
    @include hover {
      color: inherit;
    }
  }
  &__icon {
    font-size: 18px;
    margin-bottom: -2px;
  }
  &__changelog-badge {
    position: absolute;
    top: -2px;
    right: 23px;
    scale: 0.8;

    &__container {
      position: relative;
    }
  }

  &__night-mode-toggle-button {
    width: 34px;
    height: 20px;
    background-color: $c-border-2;
    border-radius: 100px;
    padding: 2px;
    transition: all 0.2s ease-in-out;
    position: relative;
    &:before {
      display: inline-block;
      content: '';
      width: 16px;
      height: 16px;
      background-color: $c-white;
      border-radius: 50%;
      transition: all 0.2s ease-in-out;
      position: absolute;
      top: 2px;
      left: 2px;
      right: auto;
    }
    &--active {
      background-color: $c-yellow;
      text-align: right;
      &:before {
        left: calc(100% - 19px);
      }
    }
  }
}

.night-mode .user-menu {
  &__toggle {
    color: $c-dark-4;
    @media only screen and (max-width: $w-tablet-s) {
      color: $c-white;
    }
    @include hover {
      color: $c-white;
    }
  }
  &__avatar {
    border-color: $c-dark-3;
    @media only screen and (max-width: $w-tablet-s) {
      border-color: $c-dark-5;
    }
  }
  &__list {
    background-color: $c-dark-2;
    &:before {
      background-color: $c-dark-2;
    }
  }
  &__item {
    &--logout {
      .user-menu__link {
        color: $c-border-8;
      }
    }
    &--night-mode {
      border-color: $c-dark-3;
    }
    @include hover {
      color: $c-white;
      @media only screen and (min-width: $w-tablet-b) {
        background: rgba($color: $c-black-9, $alpha: 0.5);
      }
      @media only screen and (max-width: $w-tablet-s) {
        color: $c-white;
      }
    }
  }
}
</style>
